@import "@/scss/use-bootstrap-mixins.scss";

.overlay {
  position: fixed;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.6);
  z-index: 999999999; // Do not lower this or it will trigger a global crisis
  padding: 16px;
}

.card {
  position: relative;
  width: 100%;
  max-width: 380px;
  border: 1px solid #f5f5f5;
  border-radius: 20px;
  padding: 20px;
  background: #ffffff;
  box-shadow: 0 21px 34.9px 0 rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  max-height: 90vh;

  @include media-breakpoint-up(lg) {
    max-width: 684px;
    padding: 30px;
  }
}

// Sits above the scrolling layout so it stays reachable on short viewports.
.closeButton {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 1;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;

  .icon {
    stroke: #434343;
    width: 14px;
    height: 14px;
  }

  @include media-breakpoint-up(lg) {
    top: 22px;
    right: 22px;
  }
}

// Mobile stacks title / unit summary / form. Desktop puts the unit summary in a
// fixed left column that spans both rows, with the title above the form on the
// right — hence the grid areas rather than nested flex.
.layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-areas:
    "title"
    "media"
    "form";
  gap: 20px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  min-height: 0;

  @include media-breakpoint-up(lg) {
    grid-template-columns: auto minmax(0, 1fr);
    grid-template-rows: auto 1fr;
    grid-template-areas:
      "media title"
      "media form";
    column-gap: 30px;
    row-gap: 15px;
  }
}

.title {
  grid-area: title;
  font-size: 20px;
  font-weight: 500;
  line-height: 1.3;
  color: #0f0f0f;
  text-align: center;
  padding-right: 24px; // keep long titles clear of the close button

  @include media-breakpoint-up(lg) {
    text-align: left;
    padding-right: 0;
  }
}

.media {
  grid-area: media;
  display: flex;
  flex-direction: column;
  gap: 15px;
  min-width: 0;

  @include media-breakpoint-up(lg) {
    width: 250px; // 219px of content + the 30px gutter before the divider
    align-self: stretch;
    justify-content: center;
    padding-right: 30px;
    border-right: 1px solid #f5f5f5;
  }
}

// Image beside the unit details on mobile, stacked above them on desktop.
.mediaTop {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 15px;

  @include media-breakpoint-up(lg) {
    flex-direction: column;
    align-items: stretch;
  }
}

// flex-basis 0 on both this and .unitTypeDetails splits the mobile row evenly.
// Both reset at lg, where .mediaTop stacks them in a column.
.imageFrame {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 0;
  min-width: 0;
  background: #f5f5f5;
  border-radius: 15px;
  padding: 15px;

  @include media-breakpoint-up(lg) {
    flex: 0 0 auto;
  }
}

.unitImage {
  width: 100%;
  height: 140px;
  object-fit: contain;

  @include media-breakpoint-up(lg) {
    height: 120px;
  }
}

.unitTypeDetails {
  display: flex;
  flex-direction: column;
  gap: 15px;
  flex: 1 1 0;
  min-width: 0;

  @include media-breakpoint-up(lg) {
    flex: 0 0 auto;
  }
}

.unitTypeName {
  font-size: 12px;
  font-weight: 500;
  line-height: 1.3;
  color: #0f0f0f;

  @include media-breakpoint-up(lg) {
    font-size: 15px;
  }
}

// Label / value pairs stack in one column beside the image on mobile and split
// into two columns under it on desktop. font-weight and line-height need
// !important throughout: #details-modal span in typography.scss reverts them.
.features {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 12px;

  @include media-breakpoint-up(lg) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }

  .block {
    display: flex;
    flex-direction: column;
    gap: 7px;
    min-width: 0;
  }

  .label {
    font-size: 10px;
    font-weight: 500 !important;
    line-height: 1.2 !important;
    color: #0f0f0f;

    @include media-breakpoint-up(lg) {
      font-size: 12px;
    }
  }

  .value {
    font-size: 10px;
    font-weight: 400 !important;
    line-height: 1.2 !important;
    color: #808080;

    @include media-breakpoint-up(lg) {
      font-size: 12px;
    }
  }
}

.formArea {
  grid-area: form;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;

  .successMessage {
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 100%;
    align-items: center;
    gap: 8px;
    max-width: 300px;
    margin: 0 auto;
    padding: 24px 0;

    .icon {
      fill: $primaryOnBackground;
    }

    .text {
      font-size: 24px;
      color: $primaryOnBackground;
      text-align: center;
    }
  }

  .form {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 13px;

    // Full width on mobile; paired side by side from lg up.
    .block {
      display: flex;
      flex-direction: column;
      gap: 6px;
      flex: 1 1 100%;
      min-width: 0;

      @include media-breakpoint-up(lg) {
        flex: 1 1 45%;
      }

      &.blockFullWidth {
        flex: 1 1 100%;
      }

      .label {
        color: #808080;
        font-size: 12px;
        font-weight: 500;
      }

      .input {
        background-color: #f5f5f5;
        border-radius: 7px;
        padding: 8px 11px;
        border: 0;
        font-size: 12px;
        color: #0f0f0f;
      }

      .inputError {
        border: 1px solid red;
      }

      .emailPills {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
      }

      .emailPill {
        padding: 4px 10px;
        border: 1px solid #d0d0d0;
        border-radius: 20px;
        background: #f5f5f5;
        font-size: 12px;
        color: #555;
        cursor: pointer;
        transition: all 0.2s;
        white-space: nowrap;

        &:hover {
          background: #e8e8e8;
          border-color: #999;
        }
      }

      .error {
        color: red;
        font-size: 12px;
      }
    }
  }

  // Submission failure (e.g. the API rejecting an unavailable unit type),
  // shown above the CTAs so the user knows why nothing happened.
  .submitError {
    flex: 1 1 100%;
    color: red;
    font-size: 12px;
  }

  // Stacked at every breakpoint: the design puts Pay Later directly under Pay Now.
  // Button sizing mirrors .pricingContainer in the unit type card stylesheet so
  // the popup CTAs match the ones on the cards behind it.
  .buttonGroup {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1 1 100%;
    margin-top: 7px;

    [role="ui-button"] {
      width: 100%;
      border-radius: 100px;
      padding: 12px 16px;
      font-size: 16px;

      @include media-breakpoint-up(lg) {
        padding: 9px 16px;
        font-size: 14px;
      }
    }
  }

  .policy_links {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    justify-content: center;
    padding-top: 4px;
  }

  .policy_link {
    font-size: 10px;
    color: #434343;
    text-decoration: underline;
    text-underline-offset: 2px;

    &:hover {
      color: $primary;
    }
  }
}
